import { useState, useEffect } from 'react';
import { ManageForm } from '@components/forms/ManageForm';
import { FormActions } from '@components/forms/FormActions';
import { FormRequiredText } from '@components/forms/text/base/FormRequiredText';
import { FormSelect } from '@components/forms/dropdowns/base/FormSelect';
import {
    useGetEmployeeQuery,
    useUpdateEmployeeMutation,
    useCreateEmployeeMutation,
} from '@features/redux/apis/employees';
import { OrgDropDowns } from '@components/forms/dropdowns/OrgDropdowns';
import { Grid, Paper, Typography } from '@mui/material';
import type { ManageProps } from '../Panel';
import { Operation } from '@/types/general';
import { useSelector } from 'react-redux';
import type { RootState } from '@features/redux/store';
import { Query } from '@features/redux/apis/utils';
import { Validate } from '@utils/validate';
import { useLocationMapQuery, useNameMapQuery, useOrgMapQuery } from '@features/redux/apis/org';

// Create a custom withDraft for employee form
const useEmployeeDraft = Query.withDraft(useGetEmployeeQuery, {
    Name: '',
    Email: '',
    Status: 'Active' as const,
    LocationID: null,
    CompanyID: null,
    ResellerID: null,
    EmployeeID: '',
});

const statusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
];

export const Employee = ({ operation, id, onSuccess }: ManageProps & { id?: string }) => {
    const { IdToken } = useSelector((state: RootState) => state.auth);

    const {
        draft,
        data: apiData,
        isFetching: isLoading,
    } = useEmployeeDraft({ id: id! }, { skip: !id || operation === Operation.Create });

    const [draftState, setDraftState] = draft;

    const { isLoading: nameMapLoading } = useNameMapQuery({ token: IdToken! }, { skip: !IdToken });
    const { isLoading: locationMapLoading } = useLocationMapQuery({ token: IdToken! }, { skip: !IdToken });
    const { isLoading: orgLoading } = useOrgMapQuery({ token: IdToken! }, { skip: !IdToken });

    const [updateEmployee, { isLoading: saving }] = useUpdateEmployeeMutation();
    const [createEmployee, { isLoading: creating }] = useCreateEmployeeMutation();

    useEffect(() => {
        if (operation === Operation.Create) {
            // Clear form for create mode
            setDraftState({
                Name: '',
                Email: '',
                LocationID: null,
                Status: 'Active',
                CompanyID: null,
                ResellerID: null,
            });
        } else if (apiData) {
            setDraftState({
                Name: apiData.Name,
                Email: apiData.Email,
                Status: apiData.Status,
                // Parse org data from employee (if available in expanded response)
                CompanyID: apiData.CompanyID,
                ResellerID: apiData.ResellerID,
                LocationID: apiData.LocationID,
            });
        }
    }, [apiData, operation, setDraftState]);

    const handleSave = async () => {
        const employeeData = {
            Name: draftState.Name,
            Email: draftState.Email,
            LocationID: draftState.LocationID!,
            Status: draftState.Status,
        };

        if (operation === Operation.Create) {
            await createEmployee({ token: IdToken!, employee: employeeData }).unwrap();
        } else {
            await updateEmployee({ token: IdToken!, id: id!, employee: employeeData }).unwrap();
        }
        onSuccess();
    };

    const handleCancel = () => {
        onSuccess();
    };

    const readOnly = operation === Operation.View;
    const isFormLoading = isLoading || saving || creating || nameMapLoading || locationMapLoading || orgLoading;
    const title = operation === Operation.Create ? 'Create New Employee' : 'Manage Employee';

    // Validation
    const isFormValid = draftState.Name?.trim() && draftState.Email?.trim() && draftState.LocationID;

    return (
        <ManageForm title={title} loading={isFormLoading} size={12}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <Paper elevation={0} sx={{ width: '100%', padding: 1, border: '1px solid #e0e0e0' }}>
                    <Typography fontSize={'18px'} pt={1} pl={1} pb={1} fontWeight={1000} color={'black'}>
                        Employee Details
                    </Typography>

                    <Grid container spacing={2} p={1}>
                        <FormRequiredText
                            size={12}
                            stateKey='Name'
                            label='Employee Name'
                            disabled={readOnly}
                            draft={draft}
                            validateFn={Validate.name}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid employee name',
                            }}
                        />

                        <FormRequiredText
                            size={12}
                            stateKey='Email'
                            label='Email Address'
                            type='email'
                            disabled={operation !== Operation.Create}
                            draft={draft}
                            validateFn={(email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid email address',
                            }}
                        />

                        <OrgDropDowns
                            disabled={readOnly}
                            size={12}
                            options={{
                                companies: true,
                                resellers: true,
                                locations: true,
                            }}
                            draft={draft}
                        />

                        <FormSelect
                            size={12}
                            label='Status'
                            disabled={readOnly}
                            draft={draft}
                            stateKey='Status'
                            options={statusOptions}
                        />
                    </Grid>
                </Paper>
            </Grid>

            {!readOnly && (
                <Grid item xs={12} sm={12} md={12} display='flex' gap='10px'>
                    <FormActions
                        onSave={handleSave}
                        onCancel={handleCancel}
                        disabled={!isFormValid || saving || creating}
                        isUpdate={operation !== Operation.Create}
                        tooltip={!isFormValid ? 'Please fill all required fields.' : ''}
                    />
                </Grid>
            )}
        </ManageForm>
    );
};
